import { api } from '../client'
import { robotMediaResolveApi } from './robot-media-resolve.api'
import { robotTrackerApi } from './robot-tracker.api'

export type {
  CreatedRobot,
  CreateTrackerInput,
  RobotTrack,
  RobotTrackPoint,
  RobotTrackQuery,
  TrackerProfileInput,
} from './robot-tracker.api'

export interface RobotAlertMutationResponse {
  status: string
}

/**
 * `pending` = a helmet registered by the USB setup wizard that has not yet bootstrapped with its
 * credential. The broker flips it to `active` on its first credentialed `get_config`.
 */
export type RobotStatus = 'active' | 'disabled' | 'deleted' | 'pending'

/** The statuses `POST /robots/{robotId}` accepts. `pending` is set only at creation, never by a patch. */
export type RobotSettableStatus = 'active' | 'disabled'

/**
 * The tenant-scoped roster shape returned by GET /robots (S1 backend; the
 * handler `.select('-apiTokenHash')` + filters status != deleted). `cameraId`
 * is the optional RobotDevice↔CctvCamera link (S1); the fleet_cctv element (S7)
 * maps a selected robot → its cameraId.
 */
/**
 * What kind of field device this is.
 *
 * Optional because `GET /robots` only began emitting it alongside the smart-helmet work; an older
 * backend omits it and the console must read that as a robot rather than crashing.
 */
export type FieldDeviceKind = 'robot' | 'helmet' | 'tracker'

/** One capture a field device produced. */
/**
 * `GET /robots/{robotId}/media`. `media` is the newest window of EACH tab (photos; clips and audio),
 * merged newest first. `totals` is the real count per tab — absent from an older backend.
 */
export interface RobotMediaList {
  media: RobotMediaItem[]
  totals?: { photos: number; videos: number }
}

export interface RobotMediaItem {
  id: string
  robotId: string
  kind: 'image' | 'video' | 'audio'
  /**
   * `on_device` — the device holds it and has not been asked for it. Such a row has NO `key`: it
   * exists so an operator can see the clip and decide whether it is worth the wearer's metered 4G.
   */
  status: 'on_device' | 'fetching' | 'stored' | 'failed'
  /** Opaque storage key. Absent while `on_device`; resolve it to a URL only when rendering. */
  key?: string
  /**
   * A small JPEG of a photo, for grids. Absent for clips, audio, and photos stored before
   * thumbnails existed (or whose thumbnail could not be made) — a tile then uses `key`.
   */
  thumbKey?: string
  /** The device's own filename, for a clip that has only been listed. */
  deviceFile?: string
  contentType?: string
  sizeBytes?: number
  durationMs?: number
  /** The alarm this is evidence for, when it arrived with one. */
  alertId?: string
  /**
   * That alarm's type and message (`ai_no_hard_hat`, "No hard hat detected (camera)"). A photo with
   * none of the three was named by no alarm: a manual capture. See `robot-media-capture.ts`.
   */
  alertType?: string
  alertMessage?: string
  /** Where the device said it was when it announced the picture; absent when it sent no geotag. */
  lng?: number
  lat?: number
  /**
   * The fetch outcome. `failureReason` distinguishes a request that never landed from one the
   * device refused (its own words in `failureDetail`) from one it accepted and never finished —
   * three different operator actions the old single hardcoded message collapsed into one.
   * `fetchTimeoutMinutes` is what the broker ACTUALLY waited; the console cannot compute it.
   */
  fetchRequestedAt?: string
  /** The device's §8.4.2 "Upload Started" — present means the helmet accepted the request. */
  fetchAckedAt?: string
  failureReason?: 'undeliverable' | 'device_rejected' | 'rejected' | 'not_requested' | 'timeout'
  failureDetail?: string
  fetchTimeoutMinutes?: number
  capturedAt: string
}

export interface RobotSummary {
  id: string
  name: string
  model?: string
  /** Absent from older backends — treat as `'robot'`. */
  kind?: FieldDeviceKind
  /**
   * The wearer's name, for a helmet. Absent on robots, and on helmets with no profile.
   *
   * The alert inbox prefers this over `name` for a helmet: `name` labels the DEVICE, and an
   * operator answering an alarm needs the person.
   */
  wearerName?: string
  /** 2 high-end (camera + media), 3 low-end (no media surface), 5 cycling. Helmets only. */
  capType?: number
  /**
   * What the firmware did the last time a voice call was tried — `yes` it joined, `unanswered`
   * it never did. Absent until a call has been attempted. Lets the SOS popup warn before the click.
   */
  callSupport?: 'yes' | 'unanswered'
  status: RobotStatus
  serverId: string
  lastSeenAt?: string
  /**
   * When this device last completed the helmet bootstrap (`get_config`).
   *
   * Reported SEPARATELY from `lastSeenAt` and never merged with it: they answer different
   * questions. `lastSeenAt` is written only on the broker's socket path, so it means "it is
   * talking to us right now"; this means "it could reach us", which is all a bootstrap proves.
   * Merging them once made a helmet whose socket was down read "online · Just now".
   */
  lastConfigAt?: string
  /** A tracker's IMEI (15 digits). Trackers only. */
  imei?: string
  /** A tracker's configured send period in seconds. Trackers only — it widens the online window. */
  sendPeriodS?: number
  cameraId?: string
  /** The helmet's own IANA timezone (it names its clips in local time). Helmets only. */
  timezone?: string
  /** Model recorded at the helmet's most recent bootstrap; absent until one has happened (never defaulted). */
  helmetModel?: 'rd-h26' | 'caps-android'
  /** RD-H26 camera AI thresholds the bootstrap sends (0..1). Absent when none are set. Helmets only. */
  aiAlarmThresholds?: HelmetAiThresholds
  /** Helmet Device_Id (IMEI). Present only for Write_Role callers. */
  deviceId?: string
  createdAt: string
}

/**
 * The RD-H26 camera AI's alarm thresholds: the confidence a detection needs before the camera
 * raises the alarm. Higher means fewer alarms; an unset one leaves the camera at its 0.25 default.
 */
export interface HelmetAiThresholds {
  /** Code 56, no hard hat. */
  noHardHat?: number
  /** Code 57, unsafe behaviour. */
  unsafeBehaviour?: number
  /** Code 58, no reflective vest. */
  noReflectiveVest?: number
}

/** What the USB setup wizard registers: a helmet created `pending` until it first bootstraps. */
export interface CreateHelmetInput {
  name: string
  serverId: string
  deviceId: string
  wearerName?: string
}

/** Both setup-wizard calls wait this long; a slow backend must not leave the wizard spinning forever. */
const PROVISIONING_TIMEOUT_MS = 30_000

/**
 * `POST /robots/{robotId}` — every field optional; send only what changed. `cameraId: null`
 * unlinks. `tracker` and `helmet` are kind-specific: the backend 404s a profile patch aimed at the
 * other kind, and 400s one request carrying both.
 */
export interface RobotUpdatePatch {
  cameraId?: string | null
  name?: string
  status?: RobotSettableStatus
  timezone?: string
  tracker?: { sendPeriodS: number }
  /** `aiAlarmThresholds`: a number sets one, `null` clears it. Applied on the helmet's next bootstrap. */
  helmet?: {
    wearerName?: string
    aiAlarmThresholds?: { [K in keyof HelmetAiThresholds]?: number | null }
  }
}

/**
 * A face enrolled in the operator gallery (face-rec C4). `faceId` is the Mongo
 * `_id`; `imageRef` is an opaque gisify media key
 * (`robots/<tenant|central>/gallery/<uuid>.<ext>`) resolved to a thumbnail via
 * `robotsApi.resolveSnapshot` (same resolver as alert snapshots). `robotId` is
 * the optional per-robot scope (absent = applies to the whole tenant fleet).
 */
export interface GalleryFace {
  faceId: string
  name: string
  role: 'friend' | 'target'
  robotId?: string
  imageRef: string
  createdAt: string
}

/**
 * A SLAM map the robot PUBLISHED to gisify (`GET /robots/maps`) — the stored copy of a map that
 * otherwise exists only on the dog.
 *
 * Why this exists: map keyframes reach us over the live `robot:{id}:lidar` tunnel, which the
 * broker fans out WITHOUT persistence — so with the robot powered off there is nothing to draw.
 * `mapRef` resolves (via `resolveMap`) to the robot's own `zlib-palette-v1` keyframe JSON, which
 * `core/occupancy-grid.ts` decodes exactly as it does a live frame; the stored map therefore
 * renders identically to the live one.
 *
 * `activeOnRobotAt` is the robot's own active pointer MIRRORED at upload time. The robot stays
 * authoritative (it boots and localizes with no network), so treat this as a display hint that can
 * legitimately be stale for a dog that has been offline.
 */
export interface StoredRobotMap {
  mapId: string
  robotId: string
  name: string
  mapRef: string
  savedAt?: string
  resolution?: number
  width?: number
  height?: number
  origin?: { x: number; y: number; yaw: number }
  occCells?: number
  freeAreaM2?: number
  mappingSeconds?: number
  lastPose?: number[]
  geoAnchor?: { lat0: number; lon0: number; heading: number }
  sizeBytes?: number
  activeOnRobotAt?: string
}

/**
 * Robot-fleet operator endpoints. Alert ack/resolve mutations (Robot Fleet
 * Alerts Phase 2), the snapshot resolver (C2), and the face-gallery CRUD (C4).
 * Each ack/resolve takes the Mongo `_id` of the alert as the path id; the
 * backend dual-mutates Mongo (system of record) then best-effort the PostGIS
 * render mirror, and is idempotent (200 on a no-op).
 */
export interface ListedRobotAlert {
  alertId: string
  robotId: string
  type?: string
  severity?: 'info' | 'warning' | 'critical'
  identity?: string
  message?: string
  status: string
  lat?: number
  lng?: number
  snapshotRef?: string
  recordedAt: string
  /** Presence-dedup: when this presence was last seen. Defaults to `recordedAt`. */
  lastSeenAt: string
  /** Presence-dedup: how many times this presence has been seen; >= 2 means a repeat. */
  sightingCount: number
  /**
   * Operator-facing device label. Absent on alerts written before the Alert schema declared a
   * `name` path — the store keeps whatever it already holds, and the inbox falls back to the
   * roster, so an old alert still resolves to a person rather than an ObjectId.
   */
  name?: string
  /** Individual occurrence times, oldest first. Empty for alerts predating the field. */
  sightings?: string[]
  /** When a critical alarm rang out unanswered. Kept after it is handled, so the log can say so. */
  missedAt?: string
  /** When an operator declined it on the ringing card. A declined alarm is never missed. */
  declinedAt?: string
  /** Each sighting's own photo, at the same ISO time as its `sightings` entry. */
  sightingSnapshots?: { at: string; ref: string }[]
}

/**
 * `GET /robots/alerts`. Asked with `include: 'mediaUrls'`, `mediaUrls` maps photo keys the rows name
 * to presigned links the server has already signed (each through its own resolver's gate, newest
 * first, capped); a key it does not cover is resolved as before. Absent when not asked for, and from
 * a backend older than the field.
 */
export interface ListAlertsResponse {
  alerts: ListedRobotAlert[]
  mediaUrls?: Record<string, string>
}

/**
 * Which alerts to list. Omitted = every actionable one (open + acknowledged). `all` is what the
 * alert LOG asks for: it keeps a handled alert on screen with its final state.
 */
export interface ListAlertsQuery {
  robotId?: string
  status?: 'open' | 'acknowledged' | 'resolved' | 'all'
  /**
   * `mediaUrls`: also sign the photos the rows name. Only the alert log asks. The 5 s poll must not:
   * it runs in every visible tab, renders no photo, and the links would be about ten times its rows.
   */
  include?: 'mediaUrls'
}

export const robotsApi = {
  /** Tracker registration (`create`) + per-device track history (`getTrack`) — robot-tracker.api.ts. */
  ...robotTrackerApi,

  /**
   * Media key → presigned URL resolvers (snapshots, helmet captures, maps; single + batch), one per
   * folder — robot-media-resolve.api.ts.
   */
  ...robotMediaResolveApi,

  /**
   * The tenant's actionable alerts, straight from the database.
   *
   * The live channel is still the fast path; this is the reconciliation path. A dropped socket or a
   * sleeping tab leaves the pushed list silently stale, and a stale alert list is the one kind of
   * staleness that actually matters here.
   */
  listAlerts: (query: ListAlertsQuery = {}) => {
    const params = new URLSearchParams()

    if (query.robotId) params.set('robotId', query.robotId)
    if (query.status) params.set('status', query.status)
    if (query.include) params.set('include', query.include)

    const qs = params.toString()

    return api.get<ListAlertsResponse>(`/robots/alerts${qs ? `?${qs}` : ''}`)
  },
  acknowledgeAlert: (alertId: string) =>
    api.post<RobotAlertMutationResponse>(`/robots/alerts/${encodeURIComponent(alertId)}/acknowledge`, {}),
  resolveAlert: (alertId: string) =>
    api.post<RobotAlertMutationResponse>(`/robots/alerts/${encodeURIComponent(alertId)}/resolve`, {}),

  /**
   * Record that this operator will NOT take an emergency (#1272 Decline).
   *
   * Deliberately NOT `resolveAlert`. Resolving says the incident is over and clears it from the
   * map and the inbox; declining says only that this person is not the one answering it, so the
   * alarm stays open and someone else still has to. Collapsing the two would let one operator
   * close a fall alarm nobody has attended.
   */
  declineAlert: (alertId: string) =>
    api.post<RobotAlertMutationResponse>(`/robots/alerts/${encodeURIComponent(alertId)}/decline`, {}),

  /**
   * "Clear all" — resolve EVERY open/acknowledged alert in a scope, server-side
   * by filter (`POST /robots/alerts/resolve-all` with `all:true`). Unlike an id
   * list, this reaches the whole backlog — including alerts beyond the FE's ≤50
   * snapshot cap — so cleared alerts don't re-seed on refresh. `robotId` narrows
   * to one robot and `severity` mirrors the inbox filter; both omitted = the
   * whole tenant. Cognito write-role + valid license; returns the count actually
   * transitioned.
   */
  resolveAllAlerts: (
    // The BACKEND vocabulary. `resolve-all` turns this into a Mongo `$in`, and the console's old
    // `warning` was a value no backend has ever written — so a filtered Clear-all matched nothing
    // while the dialog reported success.
    filter: { robotId?: string; severity?: Array<'critical' | 'high' | 'medium' | 'low' | 'info'> } = {},
  ) =>
    api.post<{ resolved: number }>('/robots/alerts/resolve-all', {
      all: true,
      ...(filter.robotId ? { robotId: filter.robotId } : {}),
      ...(filter.severity && filter.severity.length > 0 ? { severity: filter.severity } : {}),
    }),

  /**
   * Register a helmet as `pending` (`POST /robots` with `kind:'helmet'`, `pending:true`).
   *
   * Returns ONLY the id. The response also carries the device's one-time token; it is dropped here
   * and never returned, stored or logged — the wizard obtains the credentialed Config_Host from
   * `provisioning` instead, so the raw token never reaches UI state.
   */
  createHelmet: async (input: CreateHelmetInput): Promise<{ robotId: string }> => {
    const res = await api.post<{ id: string }>(
      '/robots',
      {
        kind: 'helmet',
        pending: true,
        name: input.name,
        serverId: input.serverId,
        helmet: { deviceId: input.deviceId, ...(input.wearerName ? { wearerName: input.wearerName } : {}) },
      },
      { timeout: PROVISIONING_TIMEOUT_MS },
    )

    return { robotId: String(res.data.id) }
  },

  /**
   * `POST /robots/{robotId}/provisioning` — mint the helmet's credentialed Config_Host. The caller
   * must pass the result straight to `guardConfigHost` and must never log or persist it.
   */
  provisioning: (robotId: string, deviceId: string) =>
    api.post<{ configHost: string }>(
      `/robots/${encodeURIComponent(robotId)}/provisioning`,
      { deviceId },
      { timeout: PROVISIONING_TIMEOUT_MS },
    ),

  /** Tenant-scoped fleet roster (S1 `GET /robots`). */
  list: () => api.get<{ robots: RobotSummary[] }>('/robots'),

  /**
   * Field-device setter (`POST /robots/{robotId}`): link a camera (`cameraId`, or `null` to clear),
   * rename, restatus, or edit a helmet's wearer/timezone or a tracker's report interval. Cognito
   * write-role + valid license; a non-null `cameraId` is validated as a tenant CctvCamera.
   */
  update: (robotId: string, patch: RobotUpdatePatch) =>
    api.post<RobotSummary>(`/robots/${encodeURIComponent(robotId)}`, patch),

  /**
   * Soft-delete a field device (`DELETE /robots/{robotId}`): its credential stops working and its
   * IMEI is released; history, alerts and media are kept until retention.
   */
  remove: (robotId: string) =>
    api.delete<{ success: boolean; robotId: string }>(`/robots/${encodeURIComponent(robotId)}`),

  /** One device's captures, newest first. Includes clips still held on the device. */
  listMedia: (robotId: string) => api.get<RobotMediaList>(`/robots/${encodeURIComponent(robotId)}/media`),

  /**
   * Delete one UPLOADED photo or clip (`DELETE /robots/{robotId}/media/{mediaId}`, write role).
   * 409 for a capture still on the device. Alerts that name the photo are kept.
   */
  deleteMedia: (robotId: string, mediaId: string) =>
    api.delete<{ ok: true }>(`/robots/${encodeURIComponent(robotId)}/media/${encodeURIComponent(mediaId)}`),

  /**
   * List the SLAM maps the robots published to gisify (`GET /robots/maps`). Cognito-auth +
   * read-role; optional `robotId` narrows to one robot. Unlike the live registry (which arrives
   * over the lidar tunnel and needs the dog ONLINE), this works with the robot powered off.
   */
  listMaps: (robotId?: string) =>
    api.get<{ maps: StoredRobotMap[] }>('/robots/maps' + (robotId ? '?robotId=' + encodeURIComponent(robotId) : '')),

  /**
   * List the enrolled faces for the operator's tenant (C4-2 `GET /robots/gallery`).
   * Cognito-auth + read-role. Optional `robotId` narrows to the faces scoped to
   * that robot (the backend includes tenant-wide faces too). Each face's
   * `imageRef` is resolved to a thumbnail via `resolveSnapshot`.
   */
  listGallery: (robotId?: string) =>
    api.get<{ faces: GalleryFace[] }>('/robots/gallery' + (robotId ? '?robotId=' + encodeURIComponent(robotId) : '')),

  /**
   * Enroll a known face (C4-2 `POST /robots/gallery`). Cognito write-role + valid
   * license. Exactly one image source: `imageBase64` (+ `contentType`) for a
   * freshly-picked crop, or `sourceSnapshotRef` to copy an existing alert
   * snapshot (C4-4). `robotId` optionally scopes the face to one robot. Returns
   * the created face DTO.
   */
  enrollFace: (input: {
    name: string
    role: 'friend' | 'target'
    robotId?: string
    imageBase64?: string
    contentType?: string
    sourceSnapshotRef?: string
  }) => api.post<GalleryFace>('/robots/gallery', input),

  /** Remove an enrolled face by id (C4-2 `DELETE /robots/gallery/{faceId}`). */
  removeFace: (faceId: string) => api.delete<{ ok: true }>('/robots/gallery/' + encodeURIComponent(faceId)),
}
